import { FIELD_CHAR_LIMITS, CHAR_LIMIT_WARNING_THRESHOLD } from "../constants/fieldCharLimits";

/**
 * Validate a field value against character limits.
 *
 * @param {string} value - The input value
 * @param {string} fieldType - "Text", "Email", "Date", "PickList"
 * @param {{ isRequired?: boolean }} options
 * @returns {{ valid: boolean, error: string | null, charCount: number, maxLength: number }}
 */
export function validateFieldValue(value, fieldType, options = {}) {
  const limits = FIELD_CHAR_LIMITS[fieldType];
  if (!limits || !limits.maxLength) {
    return { valid: true, error: null, charCount: 0, maxLength: Infinity };
  }

  const str = value?.toString() || "";
  const charCount = str.length;
  const maxLength = limits.maxLength;
  const minLength = limits.minLength;

  // Empty + required
  if (options.isRequired && charCount === 0) {
    return { valid: false, error: "This field is required", charCount, maxLength };
  }

  // Empty + not required = valid
  if (charCount === 0) {
    return { valid: true, error: null, charCount, maxLength };
  }

  // Max exceeded
  if (charCount > maxLength) {
    return { valid: false, error: `Maximum ${maxLength} characters allowed`, charCount, maxLength };
  }

  // Min not met (only check when non-empty)
  if (charCount < minLength) {
    return { valid: false, error: `Minimum ${minLength} characters required`, charCount, maxLength };
  }

  return { valid: true, error: null, charCount, maxLength };
}

/**
 * Validate a picklist option label.
 *
 * @param {string} label
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validatePicklistOption(label) {
  const limits = FIELD_CHAR_LIMITS.PickList;
  const len = (label || "").length;

  if (len < limits.optionMinLength) {
    return { valid: false, error: "Option cannot be empty" };
  }
  if (len > limits.optionMaxLength) {
    return { valid: false, error: `Option must be ${limits.optionMaxLength} characters or less` };
  }
  return { valid: true, error: null };
}

/**
 * Truncate a string to the maxLength for the given field type.
 * Handles paste events that bypass the maxLength attribute.
 *
 * @param {string} value
 * @param {string} fieldType - "Text", "Email", etc.
 * @returns {string}
 */
export function enforceMaxLength(value, fieldType) {
  const limits = FIELD_CHAR_LIMITS[fieldType];
  if (!limits || !limits.maxLength) return value || "";
  return (value || "").slice(0, limits.maxLength);
}

/**
 * Get the color for the character counter based on usage.
 * Green when ok, red when near/at limit.
 *
 * @param {number} charCount
 * @param {number} maxLength
 * @returns {string} CSS color value
 */
export function getCounterColor(charCount, maxLength) {
  if (maxLength <= 0) return "#999";
  const ratio = charCount / maxLength;
  if (ratio >= CHAR_LIMIT_WARNING_THRESHOLD) return "#e53e3e"; // red
  return "#38a169"; // green
}
